import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import { accountError, leaveIfSignedOut } from '@/lib/platform/accounts';
import { parsePlatformControl, parseTenantProbe, type PlatformControl } from '@/lib/platform/control';
import { CONTROL_SECTIONS, controlSectionPath, isControlSection, type ControlSectionId } from '@/lib/platform/control-nav';
import {
  fieldInput,
  parsePlatformSettings,
  type PlatformSettingsPayload,
  type SettingField,
  type SettingsCategory,
} from '@/lib/platform/settings';
import { AuditPage } from '@/pages/platform/AuditPage';
import { BackupPanel, DatabaseList, JobsPanel, SignalGrid } from '@/pages/platform/ControlPanels';

type SectionId = ControlSectionId;

const fieldClass = 'mt-1 w-full rounded-xl border border-line bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-brand';

export function SettingsPage() {
  const navigate = useNavigate();
  const routeSection = useParams().section ?? '';
  const [params] = useSearchParams();
  const querySection = params.get('section') ?? '';
  const invalid = routeSection !== '' && !isControlSection(routeSection);
  const section: SectionId = isControlSection(routeSection) ? routeSection : 'overview';
  const current = CONTROL_SECTIONS.find((item) => item.id === section) ?? CONTROL_SECTIONS[0];
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const [settings, setSettings] = useState<PlatformSettingsPayload | null>(null);
  const [control, setControl] = useState<PlatformControl | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [removeKey, setRemoveKey] = useState<string | null>(null);
  const [testKind, setTestKind] = useState<'email' | 'sms' | null>(null);
  const [testTo, setTestTo] = useState('');
  const [retryId, setRetryId] = useState<string | null>(null);

  const visible = useMemo(() => visibleCategories(section, settings), [section, settings]);
  const dirty = useMemo(() => visible.some((item) => isDirty(item, draft)), [visible, draft]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api<unknown>('/api/platform/settings'), api<unknown>('/api/platform/control')])
      .then(([settingsPayload, controlPayload]) => {
        if (cancelled) return;
        const next = parsePlatformSettings(settingsPayload);
        setSettings(next);
        setControl(parsePlatformControl(controlPayload));
        setDraft(draftFrom(next));
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled || leaveIfSignedOut(err)) return;
        setError(accountError(err, 'Could not load the control center.'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isControlSection(querySection)) return;
    navigate(controlSectionPath(querySection), { replace: true });
  }, [navigate, querySection]);

  useEffect(() => {
    function onLeave(event: BeforeUnloadEvent) {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    }
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return;
    function onClick(event: MouseEvent) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const next = `${url.pathname}${url.search}`;
      if (url.pathname === window.location.pathname) return;
      event.preventDefault();
      event.stopPropagation();
      setPendingHref(next);
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dirty]);

  async function reloadControl() {
    const payload = await api<unknown>('/api/platform/control');
    setControl(parsePlatformControl(payload));
  }

  async function refreshControl() {
    setBusy(true);
    try {
      await reloadControl();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) notify(accountError(err, 'Could not refresh the control center.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function saveCategory(target: SettingsCategory, announce = true): Promise<boolean> {
    const body = categoryBody(target, draft);
    if (Object.keys(body).length === 0) {
      if (announce) notify('Nothing to save.');
      return false;
    }
    setBusy(true);
    try {
      const payload = await api<unknown>(`/api/platform/settings/${target.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      const next = parsePlatformSettings(payload);
      setSettings(next);
      setDraft((current) => mergeDraft(current, draftFrom(next), Object.keys(body)));
      if (announce) notify('Settings saved.');
      return true;
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) notify(accountError(err, 'Could not save settings.'), 'error');
      return false;
    } finally {
      setBusy(false);
    }
  }

  function applySavedSettings(savedSettings: PlatformSettingsPayload, savedKeys: string[]) {
    setSettings(savedSettings);
    setDraft((current) => mergeDraft(current, draftFrom(savedSettings), savedKeys));
  }

  async function saveVisible() {
    const jobs = visible
      .filter((item) => isDirty(item, draft))
      .map((item) => ({ item, body: categoryBody(item, draft) }))
      .filter((job) => Object.keys(job.body).length > 0);
    if (jobs.length === 0) {
      notify('Nothing to save.');
      return;
    }
    let latest: PlatformSettingsPayload | null = null;
    const savedKeys: string[] = [];
    setBusy(true);
    try {
      for (const job of jobs) {
        const payload = await api<unknown>(`/api/platform/settings/${job.item.id}`, {
          method: 'PATCH',
          body: JSON.stringify(job.body),
        });
        latest = parsePlatformSettings(payload);
        savedKeys.push(...Object.keys(job.body));
      }
      if (latest) applySavedSettings(latest, savedKeys);
      notify(jobs.length === 1 ? 'Settings saved.' : `${jobs.length} sections saved.`);
    } catch (err: unknown) {
      if (latest) applySavedSettings(latest, savedKeys);
      if (!leaveIfSignedOut(err)) notify(accountError(err, 'Could not save settings.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function replaceSecret(key: string) {
    const value = secrets[key] ?? '';
    if (value.trim() === '') {
      notify('Enter a new credential to replace the saved one.', 'error');
      return;
    }
    setBusy(true);
    try {
      const payload = await api<unknown>(`/api/platform/settings/secrets/${key}`, {
        method: 'PUT',
        body: JSON.stringify({ value }),
      });
      const next = parsePlatformSettings(payload);
      setSettings(next);
      setDraft((current) => mergeDraft(current, draftFrom(next), []));
      setSecrets((current) => ({ ...current, [key]: '' }));
      notify('Credential saved.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) notify(accountError(err, 'Could not save the credential.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function removeSecret() {
    if (!removeKey) return;
    setBusy(true);
    try {
      const payload = await api<unknown>(`/api/platform/settings/secrets/${removeKey}`, { method: 'DELETE' });
      const next = parsePlatformSettings(payload);
      setSettings(next);
      setDraft((current) => mergeDraft(current, draftFrom(next), []));
      setRemoveKey(null);
      notify('Saved credential removed.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) notify(accountError(err, 'Could not remove the credential.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function runTest(integration: 'ai' | 'email' | 'sms' | 'storage', to?: string) {
    setBusy(true);
    try {
      const result = await api<{ ok: boolean; detail: string }>(`/api/platform/settings/test/${integration}`, {
        method: 'POST',
        body: JSON.stringify(to === undefined ? {} : { to, confirm: true }),
      });
      notify(result.detail, result.ok ? 'ok' : 'error');
      if (integration === 'email' || integration === 'sms') setTestKind(null);
      await reloadControl();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) notify(accountError(err, 'The connection test failed.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function recheck(slug: string) {
    setBusy(true);
    try {
      const probe = parseTenantProbe(await api<unknown>(`/api/platform/control/tenants/${slug}/recheck`));
      setControl((current) => current && ({
        ...current,
        tenants: current.tenants.map((tenant) => tenant.slug === slug ? {
          ...tenant,
          database: probe.status === 'connected' ? 'healthy' : 'unavailable',
          detail: probe.status === 'connected' ? `Opened in ${probe.latencyMs ?? 0} ms.` : (probe.error ?? 'The tenant database could not be opened.'),
        } : tenant),
      }));
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) notify(accountError(err, 'The database check failed.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function retryJob(id: string) {
    setBusy(true);
    try {
      await api(`/api/platform/control/jobs/${id}/retry`, { method: 'POST', body: JSON.stringify({ confirm: true }) });
      setRetryId(null);
      await reloadControl();
      notify('The job was queued again.');
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) notify(accountError(err, 'The job could not be queued again.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  if (routeSection === 'history') return <Navigate to="/super-admin/settings/audit" replace />;
  if (invalid) return <Navigate to="/super-admin/settings" replace />;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="type-section text-ink">{current.label}</h2>
          <p className="max-w-3xl text-sm text-muted">{current.blurb}</p>
        </div>
        {section === 'overview' && (
          <button type="button" disabled={busy || control === null} onClick={() => void refreshControl()} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">Check again</button>
        )}
      </div>
      {error && (
        <p className="rounded-2xl border border-danger/40 bg-card px-4 py-6 text-sm text-danger">
          {error}{' '}
          <button type="button" className="underline" onClick={() => window.location.reload()}>Retry</button>
        </p>
      )}
      {settings === null && !error && <div className="h-40 animate-pulse rounded-2xl bg-card" aria-hidden="true" />}
      {control && section === 'overview' && <SignalGrid control={control} />}
      {settings && control && section === 'configuration' && (
        <div className="space-y-4">
          {visible.map((item) => (
            <CategoryForm key={item.id} category={item} draft={draft} secrets={secrets} busy={busy} mailOverride={settings.mailOverride} onDraft={(key, value) => setDraft((current) => ({ ...current, [key]: value }))} onSecret={(key, value) => setSecrets((current) => ({ ...current, [key]: value }))} onSave={() => void saveCategory(item)} onReplace={(key) => void replaceSecret(key)} onRemove={setRemoveKey} />
          ))}
          <DomainsPanel infrastructure={settings.infrastructure} />
        </div>
      )}
      {settings && control && section === 'integrations' && (
        <div className="space-y-4">
          {visible.map((item) => (
            <CategoryForm
              key={item.id}
              category={item}
              draft={draft}
              secrets={secrets}
              busy={busy}
              mailOverride={settings.mailOverride}
              onDraft={(key, value) => setDraft((current) => ({ ...current, [key]: value }))}
              onSecret={(key, value) => setSecrets((current) => ({ ...current, [key]: value }))}
              onSave={() => void saveCategory(item)}
              onReplace={(key) => void replaceSecret(key)}
              onRemove={setRemoveKey}
              onTest={item.id === 'ai' ? () => void runTest('ai') : undefined}
              extra={item.id === 'email' || item.id === 'sms' ? (
                <button type="button" disabled={busy} onClick={() => { setTestTo(''); setTestKind(item.id === 'sms' ? 'sms' : 'email'); }} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">
                  {item.id === 'sms' ? 'Send test SMS' : 'Send test email'}
                </button>
              ) : undefined}
            />
          ))}
          <StoragePanel infrastructure={settings.infrastructure} busy={busy} onTest={() => void runTest('storage')} />
        </div>
      )}
      {control && section === 'databases' && <DatabaseList control={control} busy={busy} onRecheck={(slug) => void recheck(slug)} />}
      {control && section === 'jobs' && <JobsPanel control={control} busy={busy} onRetry={setRetryId} />}
      {control && section === 'backups' && <BackupPanel control={control} />}
      {settings && control && section === 'security' && (
        <div className="space-y-4">
          {visible.map((item) => (
            <CategoryForm key={item.id} category={item} draft={draft} secrets={secrets} busy={busy} mailOverride={settings.mailOverride} onDraft={(key, value) => setDraft((current) => ({ ...current, [key]: value }))} onSecret={(key, value) => setSecrets((current) => ({ ...current, [key]: value }))} onSave={() => void saveCategory(item)} onReplace={(key) => void replaceSecret(key)} onRemove={setRemoveKey} />
          ))}
          <SecurityNotes infrastructure={settings.infrastructure} />
        </div>
      )}
      {section === 'audit' && <AuditPage embedded />}
      <ConfirmDialog
        open={pendingHref !== null}
        title="Discard unsaved changes?"
        description="This category has edits that are not saved."
        confirmLabel="Discard"
        onCancel={() => setPendingHref(null)}
        onConfirm={() => {
          if (settings) setDraft(draftFrom(settings));
          if (pendingHref) navigate(pendingHref);
          setPendingHref(null);
        }}
      />
      <ConfirmDialog
        open={removeKey !== null}
        title="Remove saved credential?"
        description="The environment value is used again when one is set. This does not reveal the credential."
        confirmLabel="Remove"
        pending={busy}
        onCancel={() => setRemoveKey(null)}
        onConfirm={() => void removeSecret()}
      />
      <ConfirmDialog
        open={testKind !== null}
        title={testKind === 'sms' ? 'Send a test SMS?' : 'Send a test email?'}
        description={
          <label className="block text-xs text-muted">
            Recipient
            <input value={testTo} onChange={(event) => setTestTo(event.target.value)} className={fieldClass} />
          </label>
        }
        confirmLabel="Send test"
        pending={busy}
        onCancel={() => setTestKind(null)}
        onConfirm={() => {
          if (testKind) void runTest(testKind, testTo);
        }}
      />
      <ConfirmDialog
        open={retryId !== null}
        title="Retry this failed job?"
        description="The job is queued again. Its payload is not shown."
        confirmLabel="Retry"
        pending={busy}
        onCancel={() => setRetryId(null)}
        onConfirm={() => { if (retryId) void retryJob(retryId); }}
      />
      {dirty && (
        <div className="sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-card px-4 py-3 shadow-lg">
          <p className="text-sm text-ink">Unsaved changes</p>
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={() => settings && setDraft(draftFrom(settings))} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">Discard</button>
            <button type="button" disabled={busy} onClick={() => void saveVisible()} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950">Save</button>
          </div>
        </div>
      )}
    </section>
  );
}

function CategoryForm({
  category,
  draft,
  secrets,
  busy,
  mailOverride,
  onDraft,
  onSecret,
  onSave,
  onReplace,
  onRemove,
  onTest,
  extra,
}: {
  category: SettingsCategory;
  draft: Record<string, string>;
  secrets: Record<string, string>;
  busy: boolean;
  mailOverride: boolean;
  onDraft: (key: string, value: string) => void;
  onSecret: (key: string, value: string) => void;
  onSave: () => void;
  onReplace: (key: string) => void;
  onRemove: (key: string) => void;
  onTest?: () => void;
  extra?: ReactNode;
}) {
  return (
    <form
      className="space-y-4 rounded-2xl border border-line bg-card p-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      {category.id === 'email' && (
        <p className="text-xs text-muted">{mailOverride ? 'A database SMTP override is active.' : 'Mail is using the environment mailer until a host and sender address are both saved.'}</p>
      )}
      {category.settings.map((field) => (
        <Field key={field.key} field={field} draft={draft[field.key] ?? ''} secret={secrets[field.key] ?? ''} busy={busy} onDraft={onDraft} onSecret={onSecret} onReplace={onReplace} onRemove={onRemove} />
      ))}
      <button type="submit" className="sr-only" disabled={busy}>Save {category.label}</button>
      {(onTest || extra) && (
        <div className="flex flex-wrap gap-2">
          {onTest && (
            <button type="button" disabled={busy} onClick={onTest} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">Test connection</button>
          )}
          {extra}
        </div>
      )}
    </form>
  );
}

function Field({
  field,
  draft,
  secret,
  busy,
  onDraft,
  onSecret,
  onReplace,
  onRemove,
}: {
  field: SettingField;
  draft: string;
  secret: string;
  busy: boolean;
  onDraft: (key: string, value: string) => void;
  onSecret: (key: string, value: string) => void;
  onReplace: (key: string) => void;
  onRemove: (key: string) => void;
}) {
  const status = field.secret
    ? (field.configured ? `Configured (${field.source}${field.lastFour ? `, ends ${field.lastFour}` : ''})` : 'Not configured')
    : field.source;
  const inputId = `setting-${field.key}`;
  return (
    <div className="grid gap-3 border-b border-line py-4 last:border-b-0 md:grid-cols-[minmax(0,1fr)_minmax(14rem,20rem)] md:items-start">
      <div>
        <label htmlFor={inputId} className="text-sm font-semibold text-ink">{field.label}</label>
        <p className="mt-1 text-xs text-muted">{field.description}</p>
        <p className="mt-2 text-[11px] uppercase tracking-wide text-muted">{status}{field.immediate ? ' · immediate' : ''}</p>
      </div>
      {field.secret ? (
        <div className="flex flex-wrap items-center gap-2">
          <input id={inputId} type="password" autoComplete="new-password" value={secret} placeholder="Leave blank to keep the current credential" onChange={(event) => onSecret(field.key, event.target.value)} className={fieldClass} />
          <button type="button" disabled={busy} onClick={() => onReplace(field.key)} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">Replace</button>
          <button type="button" disabled={busy || field.source !== 'database'} onClick={() => onRemove(field.key)} className="rounded-full border border-danger/40 px-3 py-1.5 text-xs font-semibold text-danger">Remove</button>
        </div>
      ) : field.type === 'enum' ? (
        <select id={inputId} value={draft} onChange={(event) => onDraft(field.key, event.target.value)} className={fieldClass}>
          <option value="">Not set</option>
          {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <input
          id={inputId}
          type={field.type === 'integer' ? 'number' : field.type === 'email' ? 'email' : 'text'}
          value={draft}
          onChange={(event) => onDraft(field.key, event.target.value)}
          className={fieldClass}
        />
      )}
    </div>
  );
}

function SecurityNotes({ infrastructure }: { infrastructure: PlatformSettingsPayload['infrastructure'] }) {
  return (
    <div className="rounded-2xl border border-line bg-card p-4 text-sm text-muted">
      <p>Platform administrators must use an authenticator app. This cannot be turned off from settings.</p>
      <p className="mt-2">Session lifetime is {infrastructure.sessionLifetimeMinutes} minutes and is set with SESSION_LIFETIME. Secure cookies are {infrastructure.sessionSecureCookie === null ? 'left to the server default' : infrastructure.sessionSecureCookie ? 'required' : 'not forced'}.</p>
      <p className="mt-2">Changing the authenticator name affects the next enrollment. It does not rotate signing keys, and it does not sign anyone out.</p>
    </div>
  );
}

function StoragePanel({ infrastructure, busy, onTest }: { infrastructure: PlatformSettingsPayload['infrastructure']; busy: boolean; onTest: () => void }) {
  return (
    <div className="rounded-2xl border border-line bg-card p-4 text-sm text-muted">
      <p>The disk is <span className="font-semibold text-ink">{infrastructure.filesystemDisk}</span>. Switching disks from here would leave existing contracts and identity files behind, so the disk stays in FILESYSTEM_DISK.</p>
      <button type="button" disabled={busy} onClick={onTest} className="mt-3 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink">Test storage</button>
    </div>
  );
}

function DomainsPanel({ infrastructure }: { infrastructure: PlatformSettingsPayload['infrastructure'] }) {
  const rows = [
    ['Application URL', infrastructure.appUrl || 'Not set'],
    ['Agency base domain', infrastructure.tenantBaseDomain || 'localhost fallback'],
    ['Default agency', infrastructure.defaultAgencySlug],
    ['Extra platform hosts', infrastructure.platformHosts.join(', ') || 'None'],
    ['Environment', infrastructure.appEnv],
  ];
  return (
    <div className="space-y-3 rounded-2xl border border-line bg-card p-4 text-sm text-muted">
      {rows.map(([label, value]) => (
        <p key={label}><span className="text-ink">{label}: </span>{value}</p>
      ))}
      <p>Saving a domain here would not change DNS, Nginx, TLS certificates, or CORS. Those stay in the server environment. An agency hostname is connected from that agency’s page after the plan allows it.</p>
    </div>
  );
}

function mergeDraft(current: Record<string, string>, fresh: Record<string, string>, savedKeys: string[]): Record<string, string> {
  const saved = new Set(savedKeys);
  const merged = { ...fresh };
  for (const [key, value] of Object.entries(current)) {
    if (!saved.has(key) && value !== fresh[key]) merged[key] = value;
  }
  return merged;
}

function draftFrom(settings: PlatformSettingsPayload): Record<string, string> {
  const draft: Record<string, string> = {};
  for (const category of settings.categories) {
    for (const field of category.settings) {
      if (!field.secret) draft[field.key] = fieldInput(field);
    }
  }
  return draft;
}

function visibleCategories(section: SectionId, settings: PlatformSettingsPayload | null): SettingsCategory[] {
  if (!settings) return [];
  const ids = section === 'configuration' ? ['general'] : section === 'integrations' ? ['ai', 'email', 'sms'] : section === 'security' ? ['security'] : [];
  return settings.categories.filter((item) => ids.includes(item.id));
}

function isDirty(category: SettingsCategory | null, draft: Record<string, string>): boolean {
  if (!category) return false;
  return category.settings.some((field) => !field.secret && (draft[field.key] ?? '') !== fieldInput(field));
}

function categoryBody(category: SettingsCategory, draft: Record<string, string>): Record<string, string | number | null> {
  const body: Record<string, string | number | null> = {};
  for (const field of category.settings) {
    if (field.secret) continue;
    const value = (draft[field.key] ?? '').trim();
    if (value === fieldInput(field).trim()) continue;
    if (field.type === 'integer') {
      body[field.key] = value === '' ? null : Number(value);
      continue;
    }
    body[field.key] = value === '' ? null : value;
  }
  return body;
}
